// Evolution engine — checks thresholds, triggers evolution events,
// and applies stat upgrades. Reads evolution data from
// `src/data/evolutions.ts` (sprite-keyed, matching PartyMember.speciesId)
// and base stats from `src/data/species.ts`.
//
// UI components own the animation timeline; this module exposes the
// timing constants and a shared cancelable flag the UI can flip
// when the player holds 'X' during the sequence.

import { evolutionFor } from "@/data/evolutions";
import { resolveSpecies } from "@/engine/battle-participant";
import { maxHpFor, type PartyMember } from "@/engine/party";

export type EvolutionTrigger =
  | { kind: "level"; level: number }
  | { kind: "item"; itemId: string }
  | { kind: "trade" }
  | { kind: "friendship" };

export type EvolutionEvent = {
  fromSpeciesId: string;
  toSpeciesId: string;
  trigger: EvolutionTrigger;
  participantSnapshot: {
    nickname?: string;
    level: number;
    hpCurrent: number;
    hpMax: number;
    moves: string[];
  };
};

// Minimum shape checkEvolution needs. Accepts both a full PartyMember
// (from @/engine/party) and the lite `{ speciesId, level }` objects
// plugin-evolution passes; speciesId may be a sprite key, numeric dex id,
// or species name (anything resolveSpecies understands).
type EvolvableMember = {
  speciesId: string;
  level: number;
  nickname?: string;
  hpCurrent?: number;
  hpMax?: number;
  moves?: string[];
};

// ── checkEvolution ───────────────────────────────────────────────────────────

export function checkEvolution(
  member: EvolvableMember,
  trigger: EvolutionTrigger = { kind: "level", level: member.level },
): EvolutionEvent | null {
  // Only level-based evolutions are described by the current data.
  // Item/trade/friendship triggers have no data yet — return null.
  if (trigger.kind !== "level") return null;

  const species = resolveSpecies(member.speciesId);
  if (!species) return null;

  const entry = evolutionFor(species.sprite);
  if (!entry) return null;

  // The trigger's level is the level being reached; gate on the member too
  // to avoid evolving someone who hasn't actually leveled up yet.
  if (trigger.level < entry.atLevel) return null;
  if (member.level < entry.atLevel) return null;

  return {
    fromSpeciesId: entry.from,
    toSpeciesId: entry.to,
    trigger,
    participantSnapshot: {
      nickname: member.nickname,
      level: member.level,
      hpCurrent: member.hpCurrent ?? 0,
      hpMax: member.hpMax ?? 0,
      moves: member.moves ? [...member.moves] : [],
    },
  };
}

// ── applyEvolution ───────────────────────────────────────────────────────────

export function applyEvolution(
  member: PartyMember,
  event: EvolutionEvent,
): PartyMember {
  const toSpecies = resolveSpecies(event.toSpeciesId);
  if (!toSpecies) return member;

  const newHpMax = maxHpFor(toSpecies.sprite, member.level);

  // Preserve damage taken, not the HP ratio: a Surggie 5 HP down before
  // evolving is still exactly 5 HP down after. Evolution never faints you.
  const damage = Math.max(0, member.hpMax - member.hpCurrent);
  const newHpCurrent = member.fainted ? 0 : Math.max(1, newHpMax - damage);

  return {
    ...member,
    speciesId: toSpecies.sprite,
    hpMax: newHpMax,
    hpCurrent: newHpCurrent,
    types: [toSpecies.type1, toSpecies.type2].filter(
      (t): t is string => Boolean(t),
    ),
    stats: {
      atk: toSpecies.baseAtk,
      def: toSpecies.baseDef,
      spd: toSpecies.baseSpe,
    },
  };
}

// ── Animation timing ─────────────────────────────────────────────────────────

export const EVOLUTION_ANIM_MS = 3000;

export const EVOLUTION_STAGES: Array<{ atMs: number; label: string }> = [
  { atMs: 0, label: "flash" },
  { atMs: 600, label: "silhouette" },
  { atMs: 1800, label: "reveal" },
  { atMs: 2400, label: "celebrate" },
];

// ── Cancellation flag ────────────────────────────────────────────────────────
// The UI sets this true while the evolution animation is playing and the
// player is allowed to cancel by holding 'X'. The engine reads it before
// committing the evolution.

let evolutionCancelable = false;

export function setEvolutionCancelable(flag: boolean): void {
  evolutionCancelable = flag;
}

export function isEvolutionCancelable(): boolean {
  return evolutionCancelable;
}
